<div class="container-fluid escrow-report-page" data-test="escrow-report-page">
	<p-toast [style]="{marginTop: '80px'}"></p-toast>
	@if (formLoading) {
		<div class="progress-spinner">
			<app-spinner [formLoading]="formLoading"></app-spinner>
		</div>
	}

	<div class="page-header d-flex justify-content-between align-items-center">
		<div>
			<h2 data-test="escrow-report-title">Monthly Escrow Compliance Report</h2>
			<div class="page-subtitle">KBM Resorts / KMA Capital Corporation &mdash; Broker of Record: Josh Motto (HI / UT)</div>
		</div>
		<div class="d-flex gap-2 align-items-end">
			<div class="form-group as-of-picker">
				<label for="asOfDate">As of</label>
				<p-calendar inputId="asOfDate"
					[(ngModel)]="asOfDate"
					dateFormat="mm/dd/yy"
					[showIcon]="true"
					(onSelect)="onAsOfChange()"
					[appendTo]="'body'"
					data-test="escrow-asof-picker">
				</p-calendar>
			</div>
			<button pButton type="button"
				label="Refresh"
				icon="pi pi-refresh"
				class="p-button-text"
				(click)="loadReport()"
				data-test="escrow-refresh-btn">
			</button>
			<div class="form-group d-flex align-items-center gap-1 ms-1">
				<p-inputSwitch inputId="showTestBookings"
					[(ngModel)]="includeTestBookings"
					(onChange)="loadReport()"
					data-test="escrow-show-test-toggle">
				</p-inputSwitch>
				<label for="showTestBookings" class="ms-2 mb-0">Show test bookings</label>
			</div>
			<button pButton type="button"
				label="Payment Reconciliation"
				icon="pi pi-arrows-h"
				class="p-button-warning"
				(click)="gotoPaymentMapping()"
				data-test="escrow-payment-mapping-btn">
			</button>
			<button pButton type="button"
				label="Calculation Audit"
				icon="pi pi-table"
				class="p-button-info"
				(click)="gotoAudit()"
				data-test="escrow-audit-btn">
			</button>
			<button pButton type="button"
				label="Export to Excel"
				icon="pi pi-file-excel"
				class="p-button-success"
				[disabled]="formLoading || !filteredRows?.length"
				(click)="downloadExcel()"
				data-test="escrow-export-excel-btn">
			</button>
			<button pButton type="button"
				label="Print to PDF"
				icon="pi pi-file-pdf"
				class="p-button-danger"
				[disabled]="formLoading || !summary"
				(click)="downloadPdf()"
				data-test="escrow-export-pdf-btn">
			</button>
		</div>
	</div>

	<!-- Rule 0 compliance warning: shown only when test bookings are included -->
	<div *ngIf="includeTestBookings" class="alert alert-warning mb-3" data-test="escrow-test-warning">
		Test bookings included &mdash; not for compliance reporting.
	</div>

	<!-- KPI tile strip -->
	@if (summary) {
		<div class="kpi-strip" data-test="escrow-kpi-strip">
			<div [class]="statusClass(summary.hawaiiStatus)" data-test="escrow-tile-hawaii">
				<div class="tile-header">Hawaii</div>
				<div class="tile-status">{{ summary.hawaiiStatus }}</div>
				<div class="tile-row"><span>Required</span><strong>{{ formatCurrency(summary.hawaiiRequired) }}</strong></div>
				<div class="tile-row"><span>Actual</span><strong>{{ formatCurrency(summary.hawaiiActual) }}</strong></div>
				<div class="tile-row tile-variance"><span>Variance</span><strong>{{ formatCurrency(summary.hawaiiVariance) }}</strong></div>
				<div class="tile-footer">{{ summary.hawaiiActiveReservations }} active reservations</div>
			</div>

			<div [class]="statusClass(summary.utahStatus)" data-test="escrow-tile-utah">
				<div class="tile-header">Utah</div>
				<div class="tile-status">{{ summary.utahStatus }}</div>
				<div class="tile-row"><span>Required</span><strong>{{ formatCurrency(summary.utahRequired) }}</strong></div>
				<div class="tile-row"><span>Actual</span><strong>{{ formatCurrency(summary.utahActual) }}</strong></div>
				<div class="tile-row tile-variance"><span>Variance</span><strong>{{ formatCurrency(summary.utahVariance) }}</strong></div>
				<div class="tile-footer">{{ summary.utahActiveReservations }} active reservations</div>
			</div>

			<div [class]="statusClass(summary.combinedStatus)" data-test="escrow-tile-combined">
				<div class="tile-header">Combined</div>
				<div class="tile-status">{{ summary.combinedStatus }}</div>
				<div class="tile-row"><span>Required</span><strong>{{ formatCurrency(summary.combinedRequired) }}</strong></div>
				<div class="tile-row"><span>Actual</span><strong>{{ formatCurrency(summary.combinedActual) }}</strong></div>
				<div class="tile-row tile-variance"><span>Variance</span><strong>{{ formatCurrency(summary.combinedVariance) }}</strong></div>
				<div class="tile-footer">As of {{ formatDate(summary.asOfDate) }}</div>
			</div>

			<div class="tile tile-meta" data-test="escrow-tile-meta">
				<div class="tile-header">Operations</div>
				<div class="tile-row"><span>Open exceptions</span><strong>{{ summary.openExceptionCount || 0 }}</strong></div>
				<div class="tile-row"><span>Last transfer</span><strong>{{ summary.lastTransferDate ? formatDate(summary.lastTransferDate) : '&mdash;' }}</strong></div>
				<div class="tile-row"><span>Amount</span><strong>{{ formatCurrency(summary.lastTransferAmount) }}</strong></div>
				<div class="tile-footer">{{ summary.lastTransferSummary }}</div>
			</div>

			<div class="tile tile-exceptions" data-test="escrow-tile-exceptions">
				<div class="tile-header">Exceptions (Rule 6)</div>
				<div class="tile-status">{{ summary.exceptionsCount || 0 }} flagged</div>
				<div class="tile-row"><span>Cash held</span><strong>{{ formatCurrency(summary.exceptionsTotalCash) }}</strong></div>
				<div class="tile-row tile-variance"><span>Status</span><strong>{{ (summary.exceptionsCount || 0) > 0 ? 'NEEDS REVIEW' : 'CLEAN' }}</strong></div>
				<div class="tile-footer">Post-checkout cash held; requires refund or revenue recognition.</div>
			</div>

			<div [class]="qboReconClass()" data-test="escrow-tile-qbo-recon">
				<div class="tile-header">QBO Reconciliation</div>
				<div class="tile-status">{{ summary.qboReconStatus || 'TBD' }}</div>
				@if (summary.qboEscrowClearingBalance != null) {
					<div class="tile-row"><span>Wiki Required</span><strong>{{ formatCurrency(summary.combinedRequired) }}</strong></div>
					<div class="tile-row"><span>QBO Liability</span><strong>{{ formatCurrency(summary.qboEscrowClearingBalance) }}</strong></div>
					<div class="tile-row"><span>Trust Bank Held</span><strong>{{ summary.qboTrustBankBalance != null ? formatCurrency(summary.qboTrustBankBalance) : 'TBD' }}</strong></div>

					<div class="tile-section-divider"></div>

					<div class="tile-row tile-variance qbo-var-cleanup" title="Books cleanup needed (stale ghost reservations)">
						<span>Wiki &minus; Liability</span><strong>{{ formatCurrency(summary.wikiVsQboVariance) }}</strong>
					</div>
					@if (summary.wikiVsTrustBankVariance != null) {
						<div class="tile-row tile-variance qbo-var-funding" title="FUNDING GAP — fiduciary risk">
							<span>Wiki &minus; Trust Bank</span><strong>{{ formatCurrency(summary.wikiVsTrustBankVariance) }}</strong>
						</div>
					}
					@if (summary.liabilityVsTrustBankVariance != null) {
						<div class="tile-row tile-variance qbo-var-routing" title="Channel-routing problem (cash in non-trust accounts)">
							<span>Liability &minus; Trust Bank</span><strong>{{ formatCurrency(summary.liabilityVsTrustBankVariance) }}</strong>
						</div>
					}

					@if (summary.qboBoh5069Balance != null || summary.qboBoh1786Balance != null
						|| summary.qboWf7290Balance != null || summary.qboWf7282Balance != null
						|| summary.qboTrustClearingBalance != null) {
						<div class="tile-section-divider"></div>
						<table class="qbo-bank-breakdown" data-test="qbo-bank-breakdown">
							<tr><td>BOH-5069</td><td class="num">{{ summary.qboBoh5069Balance != null ? formatCurrency(summary.qboBoh5069Balance) : '&mdash;' }}</td></tr>
							<tr><td>BOH-1786</td><td class="num">{{ summary.qboBoh1786Balance != null ? formatCurrency(summary.qboBoh1786Balance) : '&mdash;' }}</td></tr>
							<tr><td>WF-7290</td><td class="num">{{ summary.qboWf7290Balance != null ? formatCurrency(summary.qboWf7290Balance) : '&mdash;' }}</td></tr>
							<tr><td>WF-7282</td><td class="num">{{ summary.qboWf7282Balance != null ? formatCurrency(summary.qboWf7282Balance) : '&mdash;' }}</td></tr>
							<tr><td>Trust Clearing</td><td class="num">{{ summary.qboTrustClearingBalance != null ? formatCurrency(summary.qboTrustClearingBalance) : '&mdash;' }}</td></tr>
						</table>
					}
					<div class="tile-footer">As of {{ summary.qboAsOfDate ? formatDate(summary.qboAsOfDate) : '&mdash;' }}</div>
				} @else {
					<div class="tile-row"><span>Wiki Required</span><strong>{{ formatCurrency(summary.combinedRequired) }}</strong></div>
					<div class="tile-row"><span>QBO Liability</span><strong>TBD</strong></div>
					<div class="tile-row"><span>Trust Bank Held</span><strong>TBD</strong></div>
					<div class="tile-footer">QBO MCP integration pending; will populate when reconnected.</div>
				}
			</div>
		</div>
	}

	<!-- Filters + line items table -->
	<div class="line-items-section" data-test="escrow-line-items">
		<div class="d-flex justify-content-between align-items-center filters-row">
			<h3>Reservations Contributing to Escrow Balance</h3>
			<div class="d-flex gap-2 filter-controls align-items-center">
				<p-dropdown [options]="marketOptions"
					[(ngModel)]="marketFilter"
					(onChange)="applyFilters()"
					placeholder="Market"
					data-test="escrow-market-filter">
				</p-dropdown>
				<p-dropdown [options]="statusOptions"
					[(ngModel)]="statusFilter"
					(onChange)="applyFilters()"
					placeholder="Status"
					data-test="escrow-status-filter">
				</p-dropdown>
				<!-- Change 1: Show all (including $0 payment-received) toggle -->
				<div class="show-all-toggle d-flex align-items-center gap-1" data-test="escrow-show-all-toggle">
					<p-checkbox [(ngModel)]="showZeroCashRows"
						[binary]="true"
						inputId="showZeroCash"
						(onChange)="applyFilters()">
					</p-checkbox>
					<label for="showZeroCash" class="toggle-label">Show all (incl. $0)</label>
				</div>
				<span class="row-count" data-test="escrow-row-count">{{ filteredRows?.length || 0 }} / {{ rows?.length || 0 }} rows</span>
			</div>
		</div>

		<p-table #escrowTable
			[value]="filteredRows"
			[paginator]="true"
			[rows]="50"
			[rowsPerPageOptions]="[25,50,100,250,500]"
			[showCurrentPageReport]="true"
			currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
			[globalFilterFields]="['confirmationNumber','guestName','propertyCode']"
			styleClass="p-datatable-sm escrow-table"
			dataKey="reservationId"
			[(expandedRowKeys)]="expandedRows"
			data-test="escrow-table">

			<ng-template pTemplate="caption">
				<div class="table-caption-row">
					<span class="p-input-icon-left">
						<i class="pi pi-search"></i>
						<input pInputText type="text" placeholder="Search..."
							(input)="escrowTable.filterGlobal($any($event.target).value, 'contains')"
							data-test="escrow-table-search">
					</span>
				</div>
			</ng-template>

			<ng-template pTemplate="header">
				<tr>
					<th style="width: 3rem"></th>
					<th pSortableColumn="confirmationNumber">Confirmation # <p-sortIcon field="confirmationNumber"></p-sortIcon></th>
					<th pSortableColumn="guestName">Guest <p-sortIcon field="guestName"></p-sortIcon></th>
					<th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
					<th pSortableColumn="market">Market <p-sortIcon field="market"></p-sortIcon></th>
					<th pSortableColumn="checkInDate">Check-in <p-sortIcon field="checkInDate"></p-sortIcon></th>
					<th pSortableColumn="checkOutDate">Check-out <p-sortIcon field="checkOutDate"></p-sortIcon></th>
					<!-- Change 2: renamed from "Net Cash" to "Payment Received" -->
					<th pSortableColumn="netCashHeld" class="text-right">Payment Received <p-sortIcon field="netCashHeld"></p-sortIcon></th>
					<th pSortableColumn="perNightEscrow" class="text-right">Per-Night <p-sortIcon field="perNightEscrow"></p-sortIcon></th>
					<th pSortableColumn="requiredInEscrow" class="text-right">Required <p-sortIcon field="requiredInEscrow"></p-sortIcon></th>
					<th pSortableColumn="reservationStatus">Res. Status (info) <p-sortIcon field="reservationStatus"></p-sortIcon></th>
					<th pSortableColumn="exception">Exception <p-sortIcon field="exception"></p-sortIcon></th>
					<th pSortableColumn="status">Lifecycle <p-sortIcon field="status"></p-sortIcon></th>
					<th pSortableColumn="lastEscrowChangeDate">Last Change <p-sortIcon field="lastEscrowChangeDate"></p-sortIcon></th>
				</tr>
			</ng-template>

			<ng-template pTemplate="body" let-row let-expanded="expanded">
				<tr [attr.data-test-row]="row.reservationId"
					[ngClass]="{'exception-row': row.exception, 'highlight-row': highlightResId && +row.reservationId === highlightResId}">
					<td>
						<button type="button"
							pButton
							pRipple
							[pRowToggler]="row"
							class="p-button-text p-button-rounded p-button-plain"
							[icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
							[attr.data-test-expand]="row.reservationId">
						</button>
					</td>
					<td><strong>{{ row.confirmationNumber }}</strong></td>
					<td>{{ row.guestName }}</td>
					<td>{{ row.propertyCode }}</td>
					<td>{{ row.market === 'HAWAII' ? 'HI' : 'UT' }}</td>
					<td>{{ formatDate(row.checkInDate) }}</td>
					<td>{{ formatDate(row.checkOutDate) }}</td>
					<td class="text-right">{{ formatCurrency(row.netCashHeld) }}</td>
					<td class="text-right">{{ formatCurrency(row.perNightEscrow) }}</td>
					<td class="text-right"><strong>{{ formatCurrency(row.requiredInEscrow) }}</strong></td>
					<td>{{ row.reservationStatus }}</td>
					<td>
						@if (row.exception) {
							<!-- Change 5: show stale-cancellation reason in tooltip if present -->
							<span class="exception-pill"
								[title]="row.exceptionReason || 'Post-checkout cash held (Rule 6)'"
								data-test="escrow-exception-pill">EXCEPTION</span>
						}
					</td>
					<!-- Change 3: accurate lifecycle derived from reservationStatus + dates -->
					<td>
						<span [class]="lifecycleClass(row)"
							[title]="lifecycleTooltip(row)"
							data-test="escrow-lifecycle-pill">{{ deriveLifecycle(row) }}</span>
					</td>
					<td>{{ formatDate(row.lastEscrowChangeDate) }}</td>
				</tr>
			</ng-template>

			<ng-template pTemplate="rowexpansion" let-row>
				<tr>
					<td colspan="14" class="calc-breakdown-cell">
						<div class="calc-breakdown" [attr.data-test-breakdown]="row.reservationId">
							<div class="breakdown-header">
								<strong>Reservation {{ row.confirmationNumber }}</strong>
								&mdash; {{ row.guestName }} &mdash; {{ row.propertyCode }} &mdash; {{ row.market === 'HAWAII' ? 'HI' : 'UT' }}
							</div>

							<div class="breakdown-grid">
								<!-- INPUTS -->
								<div class="breakdown-section" data-test="breakdown-inputs">
									<div class="section-title">INPUTS</div>
									<div class="kv-row"><span>Total reservation amount</span><strong class="num">{{ formatCurrency(row.totalReservationAmount) }}</strong></div>
									<div class="kv-row"><span>Refundable portion</span><strong class="num">{{ formatCurrency(row.refundableAmount) }}</strong></div>
									<div class="kv-row"><span>Non-refundable portion</span><strong class="num">{{ formatCurrency(row.nonRefundableAmount) }}</strong></div>
									<div class="kv-row"><span>Total nights</span><strong class="num">{{ formatNumber(row.totalNights) }}</strong></div>
									<div class="kv-row"><span>Check-in</span><strong class="num">{{ formatDate(row.checkInDate) }}</strong></div>
									<div class="kv-row"><span>Check-out</span><strong class="num">{{ formatDate(row.checkOutDate) }}</strong></div>
									<div class="kv-row"><span>As-of date</span><strong class="num">{{ formatDate(row.asOfDate) }}</strong></div>
									<div class="kv-row"><span>Days until check-in</span><strong class="num">{{ formatNumber(row.daysUntilCheckIn) }}</strong></div>
									<div class="kv-row"><span>Nights consumed</span><strong class="num">{{ formatNumber(row.nightsConsumed) }}</strong></div>
									<div class="kv-row"><span>Nights remaining</span><strong class="num">{{ formatNumber(row.nightsRemaining) }}</strong></div>
								</div>

								<!-- CALCULATION (locked spec: Rules 2,3,5,6) -->
								<div class="breakdown-section" data-test="breakdown-calculation">
									<div class="section-title">CALCULATION (locked spec, 6 rules)</div>
									<div class="kv-row"><span>Total Billed</span><strong class="num">{{ formatCurrency(row.totalBilled) }}</strong></div>
									<div class="kv-row"><span>Escrow-Eligible Billed</span><strong class="num">{{ formatCurrency(row.escrowEligibleBilled) }}</strong></div>
									<div class="kv-row"><span>Non-Escrow Billed</span><strong class="num">{{ formatCurrency(row.nonEscrowBilled) }}</strong></div>
									<div class="kv-row"><span>Net Cash Received</span><strong class="num">{{ formatCurrency(row.netCashHeld) }}</strong></div>
									<div class="kv-row"><span>Allocated Cash (Rule 5)</span><strong class="num">{{ formatCurrency(row.allocatedNetCash) }}</strong></div>
									<div class="kv-row"><span>Escrow Ratio</span><strong class="num">{{ formatRatio(row.escrowRatio) }}</strong></div>
									<div class="kv-row"><span>Escrow-Eligible Cash</span><strong class="num">{{ formatCurrency(row.escrowEligibleCash) }}</strong></div>
									<div class="formula-row">
										Per-Night Escrow = Eligible Cash &divide; Total Nights
									</div>
									<div class="formula-row formula-detail">
										= {{ formatCurrency(row.escrowEligibleCash) }} &divide; {{ row.totalNights || 0 }} = {{ formatCurrency(row.perNightEscrow) }}
									</div>
									<div class="kv-row"><span>Per-Night Escrow</span><strong class="num">{{ formatCurrency(row.perNightEscrow) }}</strong></div>
									<div class="kv-row"><span>Released to Date</span><strong class="num">{{ formatCurrency(row.releasedToDate) }}</strong></div>
									<div class="kv-row total-row"><span>Required in Escrow = per-night &times; nights remaining</span><strong class="num">{{ formatCurrency(row.requiredInEscrow) }}</strong></div>
									@if (row.exception) {
										<div class="formula-row" style="color:#dc2626; font-weight:600">
											@if (row.exceptionReason) {
												EXCEPTION: {{ row.exceptionReason }}
											} @else {
												EXCEPTION: post-checkout cash held; requires refund or revenue recognition (Rule 6).
											}
										</div>
									}
								</div>

								<!-- PAYMENTS -->
								<div class="breakdown-section breakdown-payments" data-test="breakdown-payments">
									<div class="section-title">PAYMENTS RECEIVED (from payment_confirm)</div>
									@if (row.paymentsReceived?.length) {
										<table class="payments-table">
											<thead>
												<tr><th class="text-right">Amount</th><th>Date</th><th>Card</th><th>Bank</th><th>Status</th></tr>
											</thead>
											<tbody>
												@for (p of row.paymentsReceived; track $index) {
													<tr>
														<td class="text-right num">{{ formatCurrency(p.amount) }}</td>
														<td>{{ formatDate(p.paymentDate) }}</td>
														<td>{{ p.cardType || '&mdash;' }}</td>
														<td>{{ p.bankAccount || '&mdash;' }}</td>
														<td><span [class]="bankTrailClass(p.bankTrailStatus)">{{ bankTrailLabel(p.bankTrailStatus) }}</span></td>
													</tr>
												}
											</tbody>
										</table>
									} @else {
										<div class="payments-empty">No payment_confirm rows for this reservation.</div>
									}
									<div class="kv-row"><span>Payments total</span><strong class="num">{{ formatCurrency(row.paymentsTotal) }}</strong></div>
									<div class="kv-row"><span>Required in escrow</span><strong class="num">{{ formatCurrency(row.requiredInEscrow) }}</strong></div>
									<div class="kv-row"><span>Bank trail</span><strong><span [class]="bankTrailClass(row.bankTrailStatus)">{{ bankTrailLabel(row.bankTrailStatus) }}</span></strong></div>
									<div class="mt-2 pt-2" style="border-top: 1px dashed #dde2ec;">
										<button pButton type="button"
											icon="pi pi-arrows-h"
											label="View Payment Trace"
											class="p-button-outlined p-button-sm p-button-warning"
											(click)="gotoPaymentMapping(row.reservationId)"
											data-test="escrow-goto-pm-btn">
										</button>
									</div>
								</div>

								<!-- QBO -->
								<div class="breakdown-section breakdown-qbo" data-test="breakdown-qbo">
									<div class="section-title">QBO LIABILITY (account 1150040022)</div>
									@if (row.qboPostedAmount != null) {
										<div class="kv-row"><span>QBO posted</span><strong class="num">{{ formatCurrency(row.qboPostedAmount) }}</strong></div>
										<div class="kv-row"><span>Discrepancy</span><strong class="num">{{ formatCurrency(row.discrepancy) }}</strong></div>
									} @else {
										<div class="qbo-pending">TBD &mdash; QBO MCP integration pending. Will populate when reconnected.</div>
									}
								</div>
							</div>
						</div>
					</td>
				</tr>
			</ng-template>

			<ng-template pTemplate="emptymessage">
				<tr>
					<td colspan="14" class="text-center empty-row">No reservations contributing to escrow on the selected date.</td>
				</tr>
			</ng-template>
		</p-table>
	</div>

	<!-- Hidden import-export helper for Excel pipeline -->
	<app-import-export
		importEnabled="false"
		[importDisabled]="true"
		[dataRetrived]="filteredRows"
		style="display:none">
	</app-import-export>
</div>
